import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';

export default function SSOCallback() {
  const navigate = useNavigate();
  const { loginUser } = useAuth();
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [isChoosingRole, setIsChoosingRole] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const callbackHandled = useRef(false);

  useEffect(() => {
    if (callbackHandled.current) return;
    callbackHandled.current = true;

    const doExchange = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const appToken = urlParams.get('token');
      const roleSelectionRequired = appToken === 'role_selection_required';

      if (roleSelectionRequired) {
        const rolesFromQuery = urlParams.get('roles');
        if (rolesFromQuery) {
          try {
            const parsedRoles = JSON.parse(decodeURIComponent(rolesFromQuery));
            setRoles(Array.isArray(parsedRoles) ? parsedRoles : []);
          } catch (err) {
            setRoles([]);
          }
        } else {
          setRoles([]);
        }
        setIsChoosingRole(true);
        setIsLoading(false);
        return;
      }

      if (!appToken) {
        toast.error('Missing sign-in token');
        return navigate('/admin/login', { replace: true });
      }

      try {
        const userRes = await api.get('/auth/me', {
          headers: { Authorization: `Bearer ${appToken}` }
        });

        loginUser(appToken, {
          id: userRes.data.id || userRes.data._id,
          name: userRes.data.name,
          email: userRes.data.email,
          role: userRes.data.role || (userRes.data.type === 'supplier' ? 'supplier' : 'user')
        });
        toast.success('Signed in with Microsoft');
        navigate('/dashboard', { replace: true });
      } catch (err) {
        toast.error(err.response?.data?.message || err.message || 'SSO login failed');
        navigate('/admin/login', { replace: true });
      }
    };

    doExchange();
  }, [loginUser, navigate]);

  const handleRoleSelection = () => {
    if (!selectedRole) {
      toast.error('Please select a role');
      return;
    }

    setIsLoading(true);
    const urlParams = new URLSearchParams(window.location.search);
    const email = urlParams.get('email');
    const entraId = urlParams.get('entraId');
    const principalName = urlParams.get('principalName');
    const fullName = urlParams.get('name');

    const currentOrigin = window.location.origin;
    const isLocalDev = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
    const backendBaseUrl = isLocalDev ? `${currentOrigin.replace(/:\d+$/, '')}:5000` : currentOrigin;
    const callbackUrl = `${backendBaseUrl}/api/auth/sso/callback?code=role_selection&role=${encodeURIComponent(selectedRole)}&email=${encodeURIComponent(email || '')}&entraId=${encodeURIComponent(entraId || '')}&principalName=${encodeURIComponent(principalName || '')}&name=${encodeURIComponent(fullName || '')}`;
    window.location.href = callbackUrl;
  };

  if (isChoosingRole) {
    return (
      <div className="auth-layout" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fff7f8 0%, #ffffff 100%)' }}>
        <div className="auth-form-panel" style={{ maxWidth: 560, margin: '0 auto', padding: '32px 20px' }}>
          <div className="auth-form-wrap" style={{ padding: '32px', borderRadius: '20px', boxShadow: '0 20px 50px -20px rgba(0,0,0,0.25)', border: '1px solid #f0e9eb' }}>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 10px', borderRadius: '999px', background: '#fff2f4', color: '#c8102e', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#c8102e' }} />
              Access portal
            </div>
            <h2 className="auth-form-title" style={{ marginTop: '16px', marginBottom: '8px', fontSize: '28px' }}>Choose your role</h2>
            <p className="auth-form-sub" style={{ marginBottom: '24px' }}>This Microsoft account is linked to more than one internal role. Pick the one you want to sign in with.</p>
            <div style={{ display: 'grid', gap: '12px' }}>
              {roles.map(roleOption => {
                const isSelected = selectedRole === roleOption.role;
                return (
                  <label
                    key={roleOption.role}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '12px',
                      padding: '14px 16px',
                      border: `1px solid ${isSelected ? '#c8102e' : '#e8e2e4'}`,
                      borderRadius: '14px',
                      cursor: 'pointer',
                      background: isSelected ? '#fff7f8' : '#ffffff',
                      boxShadow: isSelected ? '0 8px 24px -12px rgba(200,16,46,0.35)' : 'none',
                      transition: 'all 0.2s ease'
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                      <input
                        type="radio"
                        name="role"
                        value={roleOption.role}
                        checked={isSelected}
                        onChange={() => setSelectedRole(roleOption.role)}
                      />
                      <span style={{ fontWeight: 600, color: '#111827' }}>{roleOption.label}</span>
                    </div>
                    <span style={{ fontSize: '12px', color: '#6b7280' }}>Portal access</span>
                  </label>
                );
              })}
            </div>
            <button
              type="button"
              className="auth-btn"
              style={{ width: '100%', marginTop: '22px', padding: '14px 16px', borderRadius: '12px' }}
              onClick={handleRoleSelection}
              disabled={isLoading}
            >
              {isLoading ? 'Signing in...' : 'Continue'}
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-layout" style={{ minHeight: '100vh', background: 'linear-gradient(135deg, #fff7f8 0%, #ffffff 100%)' }}>
      <div className="auth-form-panel" style={{ maxWidth: 520, margin: '0 auto', padding: '32px 20px' }}>
        <div className="auth-form-wrap" style={{ padding: '32px', borderRadius: '20px', boxShadow: '0 20px 50px -20px rgba(0,0,0,0.25)', border: '1px solid #f0e9eb', textAlign: 'center' }}>
          <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', padding: '6px 10px', borderRadius: '999px', background: '#fff2f4', color: '#c8102e', fontSize: '12px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            <span style={{ width: '8px', height: '8px', borderRadius: '50%', background: '#c8102e' }} />
            Signing in
          </div>
          <h2 className="auth-form-title" style={{ marginTop: '16px', marginBottom: '8px', fontSize: '28px' }}>One moment please</h2>
          <p className="auth-form-sub">Please wait while we complete your Microsoft sign-in.</p>
          <div className="spinner" style={{ margin: '32px auto' }} />
        </div>
      </div>
    </div>
  );
}
